import { AnimatePresence, motion } from "framer-motion";
import { observer } from "mobx-react-lite";
import { RefObject, useCallback, useMemo, useState } from "react";

import { useObservabilitySidebarResizing } from "./hooks/useObservabilitySidebarResizing";
import ObservabilitySidebarResizeRail from "./ObservabilitySidebarResizeRail";

import SecondarySidebar from ".";

import { StructuredSearchProvider, useStructuredSearchStore } from "@/components/StructuredSearch";
import {
  REQUESTS_SIDEBAR_URL_NAMESPACE,
  TRACES_SIDEBAR_URL_NAMESPACE,
} from "@/components/StructuredSearch/hooks/structuredSearchUrlNamespaces";
import { useStructuredSearchURLSync } from "@/components/StructuredSearch/hooks/useStructuredSearchURLSync";
import { tracesSearchSchema } from "@/components/StructuredSearch/schemas/traces-search-schema";
import { useStructuredSidebarRequestLogs } from "@/components/SidebarSearchLayout/Sidebar/hooks";
import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import {
  applyRequestLogHydrationToItems,
  buildRequestLogStubItems,
  useRequestLogHydration,
  type RequestLogHydrationStatus,
  type RequestLogListItem,
} from "@/queries/requests/requestLogHydration";
import { requestsSearchSchema } from "@/routes/request-logs/search/requests-search-schema";
import { Request } from "@/types/requests";

const getRequestHydrationStatus = ({
  hasIds,
  isError,
  hasData,
}: {
  hasIds: boolean;
  isError: boolean;
  hasData: boolean;
}): RequestLogHydrationStatus => {
  if (!hasIds || hasData) {
    return "complete";
  }
  if (isError) {
    return "error";
  }
  return "pending";
};

const SECONDARY_SIDEBAR_SEARCH_ACTIVE_WIDTH = 598;

export interface ObservabilitySecondarySidebarDrawerProps {
  isVisible: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  requestsOn: boolean;
  onRefresh?: () => void;
  observabilityDrawerOpen: boolean;
  requestsTabActive: boolean;
}

/** Syncs the nested sidebar store to its own URL namespace. */
const SidebarStructuredSearchUrlSync = ({ requestsOn }: { requestsOn: boolean }) => {
  const store = useStructuredSearchStore();
  useStructuredSearchURLSync(
    store,
    true,
    requestsOn ? REQUESTS_SIDEBAR_URL_NAMESPACE : TRACES_SIDEBAR_URL_NAMESPACE,
  );
  return null;
};

const ObservabilitySecondarySidebarPanel = observer(
  ({
    inputRef,
    requestsOn,
    onRefresh,
    observabilityDrawerOpen,
    requestsTabActive,
  }: Omit<ObservabilitySecondarySidebarDrawerProps, "isVisible">) => {
    const structuredSearchStore = useStructuredSearchStore();
    const userToken = useUserToken();
    const workspaceId = useUser()?.activeWorkspaceId ?? 0;
    const requestLogs = useStructuredSidebarRequestLogs(
      observabilityDrawerOpen && requestsTabActive,
    );
    const stubRequests = useMemo(
      () =>
        requestLogs.data?.pages?.flatMap(page => buildRequestLogStubItems(page)) ??
        ([] as RequestLogListItem[]),
      [requestLogs.data],
    );
    const requestLogIds = useMemo(
      () => stubRequests.map(item => Number(item.id)).filter(id => Number.isFinite(id)),
      [stubRequests],
    );
    const sourceFieldsByRequestId = useMemo(() => {
      const merged: Record<string, Record<string, unknown>> = {};
      requestLogs.data?.pages?.forEach(page => {
        Object.assign(merged, page.source_fields_by_request_id ?? {});
      });
      return merged;
    }, [requestLogs.data]);
    const requestHydration = useRequestLogHydration({
      userToken,
      workspaceId,
      requestLogIds,
      sourceFieldsByRequestId,
      enabled: observabilityDrawerOpen && requestsTabActive && requestLogIds.length > 0,
    });
    const hydrationStatus = getRequestHydrationStatus({
      hasIds: requestLogIds.length > 0,
      isError: requestHydration.isError,
      hasData: Boolean(requestHydration.data),
    });
    const requests: Request[] = useMemo(
      () =>
        applyRequestLogHydrationToItems({
          stubs: stubRequests,
          hydratedItems: requestHydration.data?.items,
          status: hydrationStatus,
        }).map(item => {
          if (item?.metadata?.[0] === null) {
            return { ...item, metadata: null };
          }
          return item;
        }),
      [hydrationStatus, requestHydration.data?.items, stubRequests],
    );
    const [tracesSearchActive, setTracesSearchActive] = useState(false);
    const builderActive = structuredSearchStore.step !== "idle";
    const reviewLogsSearchActive =
      observabilityDrawerOpen &&
      requestsTabActive &&
      (structuredSearchStore.dropdownOpen ||
        structuredSearchStore.editingFilterId !== null ||
        builderActive);
    const drawerSearchActive =
      reviewLogsSearchActive ||
      (observabilityDrawerOpen && !requestsTabActive && tracesSearchActive);

    const { sidebarWidth, isResizing, startResizing } = useObservabilitySidebarResizing();
    const effectiveWidth = drawerSearchActive
      ? Math.max(sidebarWidth, SECONDARY_SIDEBAR_SEARCH_ACTIVE_WIDTH)
      : sidebarWidth;

    const handleResizeStart = useCallback(
      (event: React.MouseEvent) => {
        startResizing(
          event,
          effectiveWidth,
          drawerSearchActive ? SECONDARY_SIDEBAR_SEARCH_ACTIVE_WIDTH : 280,
        );
      },
      [drawerSearchActive, effectiveWidth, startResizing],
    );

    return (
      <motion.div
        key="secondary-sidebar"
        initial={{ width: 0, opacity: 0 }}
        animate={{ width: effectiveWidth, opacity: 1 }}
        exit={{ width: 0, opacity: 0 }}
        transition={isResizing ? { duration: 0 } : { duration: 0.3, ease: "easeInOut" }}
        className="relative z-[25] h-full flex-shrink-0"
      >
        <div className="absolute inset-0">
          <SecondarySidebar
            inputRef={inputRef}
            requestsOn={requestsOn}
            requests={requests}
            requestLogs={requestLogs}
            onRefresh={onRefresh}
            secondarySidebarOpen={observabilityDrawerOpen}
            onSearchActiveChange={setTracesSearchActive}
          />
        </div>
        <ObservabilitySidebarResizeRail onMouseDown={handleResizeStart} />
      </motion.div>
    );
  },
);

const ObservabilitySecondarySidebarDrawer = ({
  isVisible,
  requestsOn,
  ...rest
}: ObservabilitySecondarySidebarDrawerProps) => {
  const searchSchema = requestsOn ? requestsSearchSchema : tracesSearchSchema;
  const searchProviderKey = requestsOn ? "requests-sidebar-search" : "traces-sidebar-search";

  if (!isVisible) {
    return null;
  }

  return (
    <StructuredSearchProvider key={searchProviderKey} config={searchSchema}>
      <SidebarStructuredSearchUrlSync requestsOn={requestsOn} />
      <AnimatePresence initial={false}>
        <ObservabilitySecondarySidebarPanel {...rest} requestsOn={requestsOn} />
      </AnimatePresence>
    </StructuredSearchProvider>
  );
};

export default ObservabilitySecondarySidebarDrawer;
